(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initReveal(root = document) {
    const items = root.querySelectorAll('[data-z-reveal]:not([data-z-ready])');
    if (!items.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('z-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('z-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .14, rootMargin: '0px 0px -5% 0px' });
    items.forEach((item) => {
      item.dataset.zReady = 'true';
      observer.observe(item);
    });
  }

  function initPointerGlow(root = document) {
    if (reduceMotion.matches || !window.matchMedia('(pointer: fine)').matches) return;
    root.querySelectorAll('[data-z-pointer]:not([data-z-pointer-ready])').forEach((element) => {
      element.dataset.zPointerReady = 'true';
      element.addEventListener('pointermove', (event) => {
        const bounds = element.getBoundingClientRect();
        element.style.setProperty('--z-x', `${event.clientX - bounds.left}px`);
        element.style.setProperty('--z-y', `${event.clientY - bounds.top}px`);
      }, { passive: true });
    });
  }

  function initSunScience(root = document) {
    root.querySelectorAll('[data-z-science]:not([data-z-science-ready])').forEach((section) => {
      section.dataset.zScienceReady = 'true';
      const buttons = [...section.querySelectorAll('[data-z-science-button]')];
      const panels = [...section.querySelectorAll('[data-z-science-panel]')];
      const activate = (button, moveFocus = false) => {
        const target = button.dataset.zScienceButton;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => panel.hidden = panel.dataset.zSciencePanel !== target);
        section.dataset.active = target;
        if (moveFocus) button.focus();
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          const keys = { ArrowRight: index + 1, ArrowDown: index + 1, ArrowLeft: index - 1, ArrowUp: index - 1, Home: 0, End: buttons.length - 1 };
          if (!(event.key in keys)) return;
          event.preventDefault();
          activate(buttons[(keys[event.key] + buttons.length) % buttons.length], true);
        });
      });
    });
  }

  function initProductTilt(root = document) {
    root.querySelectorAll('[data-z-product-tilt]:not([data-z-product-tilt-ready])').forEach((stage) => {
      stage.dataset.zProductTiltReady = 'true';
      if (stage.dataset.enabled !== 'true' || reduceMotion.matches || !window.matchMedia('(pointer: fine)').matches) return;
      const strength = Number(stage.dataset.strength || 9);
      const visual = stage.closest('.z-science__visual');
      if (!visual) return;
      const update = (event) => {
        const bounds = visual.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
        const y = Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height));
        stage.style.setProperty('--z-tilt-x', `${(0.5 - y) * strength * 2}deg`);
        stage.style.setProperty('--z-tilt-y', `${(x - 0.5) * strength * 2}deg`);
        stage.style.setProperty('--z-glare-x', `${x * 100}%`);
        stage.style.setProperty('--z-glare-y', `${y * 100}%`);
      };
      const reset = () => {
        stage.style.setProperty('--z-tilt-x', '0deg');
        stage.style.setProperty('--z-tilt-y', '0deg');
        stage.style.setProperty('--z-glare-x', '50%');
        stage.style.setProperty('--z-glare-y', '50%');
      };
      visual.addEventListener('pointermove', update, { passive: true });
      visual.addEventListener('pointerleave', reset);
    });
  }

  function initParticles(root = document) {
    root.querySelectorAll('canvas[data-z-particles]:not([data-z-particles-ready])').forEach((canvas) => {
      canvas.dataset.zParticlesReady = 'true';
      const context = canvas.getContext('2d');
      const container = canvas.parentElement;
      if (!context || reduceMotion.matches) return;
      let particles = [];
      let frameId;
      let visible = true;
      let pointer = { x: 0, y: 0, active: false };

      const resize = () => {
        const box = canvas.getBoundingClientRect();
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = box.width * ratio;
        canvas.height = box.height * ratio;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        const count = box.width < 750 ? 28 : 68;
        particles = Array.from({ length: count }, (_, index) => ({
          x: (index * 67.3) % box.width,
          y: (index * 41.7) % box.height,
          r: 1 + (index % 4) * .55,
          drift: .12 + (index % 5) * .025,
          phase: index * .8
        }));
      };
      const draw = (time) => {
        if (!visible) { frameId = undefined; return; }
        const box = canvas.getBoundingClientRect();
        context.clearRect(0, 0, box.width, box.height);
        particles.forEach((particle) => {
          let x = particle.x + Math.sin(time * .00025 + particle.phase) * 14;
          let y = (particle.y - time * particle.drift * .012) % (box.height + 20);
          if (y < -10) y += box.height + 20;
          if (pointer.active) {
            const dx = x - pointer.x;
            const dy = y - pointer.y;
            const distance = Math.hypot(dx, dy);
            if (distance < 120 && distance > 0) {
              x += (dx / distance) * (120 - distance) * .22;
              y += (dy / distance) * (120 - distance) * .22;
            }
          }
          context.beginPath();
          context.arc(x, y, particle.r, 0, Math.PI * 2);
          context.fillStyle = 'rgba(255,255,255,.62)';
          context.fill();
        });
        frameId = requestAnimationFrame(draw);
      };
      const visibilityObserver = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        if (visible && !frameId) frameId = requestAnimationFrame(draw);
      });
      visibilityObserver.observe(canvas);
      container.addEventListener('pointermove', (event) => {
        const rect = canvas.getBoundingClientRect();
        pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top, active: true };
      }, { passive: true });
      container.addEventListener('pointerleave', () => pointer.active = false);
      new ResizeObserver(resize).observe(canvas);
      resize();
      frameId = requestAnimationFrame(draw);
      canvas.addEventListener('zovea:destroy', () => {
        cancelAnimationFrame(frameId);
        visibilityObserver.disconnect();
      }, { once: true });
    });
  }

  function initWordParticles(root = document) {
    root.querySelectorAll('canvas[data-z-word-particles]:not([data-z-word-ready])').forEach((canvas) => {
      canvas.dataset.zWordReady = 'true';
      const context = canvas.getContext('2d');
      const hero = canvas.closest('.z-hero');
      if (!context || !hero) return;

      const text = canvas.dataset.text || 'ZOVEA';
      const spacing = Number(canvas.dataset.density || 9);
      const radius = Number(canvas.dataset.radius || 160);
      const strength = Number(canvas.dataset.strength || 8);
      const pointer = { x: 0, y: 0, active: false };
      const sampleCanvas = document.createElement('canvas');
      const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
      const particleColor = 'rgba(239, 255, 193, .86)';
      let particles = [];
      let frameId;
      let isVisible = true;

      const resize = () => {
        const bounds = canvas.getBoundingClientRect();
        if (!bounds.width || !bounds.height || !sampleContext) return;
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(bounds.width * ratio);
        canvas.height = Math.round(bounds.height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);

        sampleCanvas.width = Math.round(bounds.width);
        sampleCanvas.height = Math.round(bounds.height);
        const fontSize = Math.min(bounds.height * .72, bounds.width / Math.max(text.length * .57, 1));
        sampleContext.clearRect(0, 0, bounds.width, bounds.height);
        sampleContext.fillStyle = '#000';
        sampleContext.font = `700 ${fontSize}px ${getComputedStyle(document.documentElement).getPropertyValue('--font-heading-family') || 'serif'}`;
        sampleContext.textAlign = 'left';
        sampleContext.textBaseline = 'middle';
        const measured = sampleContext.measureText(text).width;
        sampleContext.fillText(text, Math.max(0, (bounds.width - measured) / 2), bounds.height * .54);

        const pixels = sampleContext.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height).data;
        const candidates = [];
        for (let y = 0; y < bounds.height; y += spacing) {
          for (let x = 0; x < bounds.width; x += spacing) {
            if (pixels[(Math.floor(y) * sampleCanvas.width + Math.floor(x)) * 4 + 3] > 80) candidates.push({ x, y });
          }
        }
        particles = candidates.map((point, index) => ({
          baseX: point.x,
          baseY: point.y,
          x: point.x + ((index * 17) % 18) - 9,
          y: point.y + ((index * 31) % 18) - 9,
          size: index % 5 === 0 ? 1.35 : .9,
          phase: index * .12
        }));
      };

      const draw = (time = 0) => {
        const bounds = canvas.getBoundingClientRect();
        context.clearRect(0, 0, bounds.width, bounds.height);
        particles.forEach((particle) => {
          let targetX = particle.baseX;
          let targetY = particle.baseY;
          if (pointer.active) {
            const dx = particle.x - pointer.x;
            const dy = particle.y - pointer.y;
            const distance = Math.hypot(dx, dy);
            if (distance > 0 && distance < radius) {
              const force = ((radius - distance) / radius) * strength;
              targetX += (dx / distance) * force * 7;
              targetY += (dy / distance) * force * 7;
            }
          }
          if (!reduceMotion.matches) targetY += Math.sin(time * .0012 + particle.phase) * .6;
          particle.x += (targetX - particle.x) * .14;
          particle.y += (targetY - particle.y) * .14;
          context.beginPath();
          context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          context.fillStyle = particleColor;
          context.fill();
        });
        if (!reduceMotion.matches && isVisible) frameId = requestAnimationFrame(draw);
        else frameId = undefined;
      };

      const updatePointer = (event) => {
        const bounds = canvas.getBoundingClientRect();
        pointer.x = event.clientX - bounds.left;
        pointer.y = event.clientY - bounds.top;
        pointer.active = pointer.x >= 0 && pointer.x <= bounds.width && pointer.y >= 0 && pointer.y <= bounds.height;
      };
      const observer = new IntersectionObserver(([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible && !reduceMotion.matches && !frameId) frameId = requestAnimationFrame(draw);
      }, { threshold: .05 });
      observer.observe(canvas);
      hero.addEventListener('pointermove', updatePointer, { passive: true });
      hero.addEventListener('pointerleave', () => { pointer.active = false; });
      new ResizeObserver(resize).observe(canvas);
      resize();
      if (reduceMotion.matches) draw(); else frameId = requestAnimationFrame(draw);
      canvas.addEventListener('zovea:destroy', () => {
        cancelAnimationFrame(frameId);
        observer.disconnect();
      }, { once: true });
    });
  }

  function initLogoParticles(root = document) {
    root.querySelectorAll('canvas[data-z-logo-particles]:not([data-z-logo-ready])').forEach((canvas) => {
      canvas.dataset.zLogoReady = 'true';
      const context = canvas.getContext('2d');
      const hero = canvas.closest('.z-hero');
      const wrapper = canvas.closest('.z-hero__logo-mark');
      if (!context || !hero || !wrapper) return;

      const spacing = Number(canvas.dataset.density || 9);
      const radius = Number(canvas.dataset.radius || 160);
      const strength = Number(canvas.dataset.strength || 8);
      const pointer = { x: 0, y: 0, active: false };
      const image = new Image();
      const sampleCanvas = document.createElement('canvas');
      const sampleContext = sampleCanvas.getContext('2d', { willReadFrequently: true });
      let particles = [];
      let frameId;
      let isVisible = true;
      let imageLoaded = false;

      const resize = () => {
        const bounds = canvas.getBoundingClientRect();
        if (!imageLoaded || !bounds.width || !bounds.height || !sampleContext) return;
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(bounds.width * ratio);
        canvas.height = Math.round(bounds.height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        sampleCanvas.width = Math.round(bounds.width);
        sampleCanvas.height = Math.round(bounds.height);
        sampleContext.clearRect(0, 0, bounds.width, bounds.height);
        const scale = Math.min(bounds.width / image.width, bounds.height / image.height) * .94;
        const width = image.width * scale;
        const height = image.height * scale;
        const offsetX = (bounds.width - width) / 2;
        const offsetY = (bounds.height - height) / 2;
        sampleContext.drawImage(image, offsetX, offsetY, width, height);
        const pixels = sampleContext.getImageData(0, 0, sampleCanvas.width, sampleCanvas.height).data;
        const candidates = [];
        for (let y = 0; y < bounds.height; y += spacing) {
          for (let x = 0; x < bounds.width; x += spacing) {
            const pixelIndex = (Math.floor(y) * sampleCanvas.width + Math.floor(x)) * 4;
            const alpha = pixels[pixelIndex + 3];
            if (alpha > 70) candidates.push({
              x,
              y,
              color: `rgba(${pixels[pixelIndex]}, ${pixels[pixelIndex + 1]}, ${pixels[pixelIndex + 2]}, ${Math.min(alpha / 255, .94)})`
            });
          }
        }
        particles = candidates.map((point, index) => ({
          baseX: point.x,
          baseY: point.y,
          x: point.x + ((index * 19) % 20) - 10,
          y: point.y + ((index * 29) % 20) - 10,
          size: index % 4 === 0 ? 1.4 : 1,
          color: point.color,
          phase: index * .14
        }));
      };

      const draw = (time = 0) => {
        const bounds = canvas.getBoundingClientRect();
        context.clearRect(0, 0, bounds.width, bounds.height);
        particles.forEach((particle) => {
          let targetX = particle.baseX;
          let targetY = particle.baseY;
          if (pointer.active) {
            const dx = particle.x - pointer.x;
            const dy = particle.y - pointer.y;
            const distance = Math.hypot(dx, dy);
            if (distance > 0 && distance < radius) {
              const force = ((radius - distance) / radius) * strength;
              targetX += (dx / distance) * force * 7;
              targetY += (dy / distance) * force * 7;
            }
          }
          if (!reduceMotion.matches) targetY += Math.sin(time * .001 + particle.phase) * .5;
          particle.x += (targetX - particle.x) * .14;
          particle.y += (targetY - particle.y) * .14;
          context.beginPath();
          context.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          context.fillStyle = particle.color;
          context.fill();
        });
        if (!reduceMotion.matches && isVisible) frameId = requestAnimationFrame(draw);
        else frameId = undefined;
      };

      const updatePointer = (event) => {
        const bounds = canvas.getBoundingClientRect();
        pointer.x = event.clientX - bounds.left;
        pointer.y = event.clientY - bounds.top;
        pointer.active = pointer.x >= 0 && pointer.x <= bounds.width && pointer.y >= 0 && pointer.y <= bounds.height;
      };
      const observer = new IntersectionObserver(([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible && imageLoaded && !reduceMotion.matches && !frameId) frameId = requestAnimationFrame(draw);
      }, { threshold: .05 });
      observer.observe(canvas);
      hero.addEventListener('pointermove', updatePointer, { passive: true });
      hero.addEventListener('pointerleave', () => { pointer.active = false; });
      new ResizeObserver(resize).observe(canvas);
      image.onload = () => {
        imageLoaded = true;
        resize();
        if (reduceMotion.matches) draw(); else frameId = requestAnimationFrame(draw);
      };
      image.onerror = () => { wrapper.hidden = true; };
      image.src = canvas.dataset.src;
      canvas.addEventListener('zovea:destroy', () => {
        cancelAnimationFrame(frameId);
        observer.disconnect();
      }, { once: true });
    });
  }

  function initStickyAtc() {
    const sticky = document.querySelector('[data-z-sticky-atc]');
    if (!sticky || sticky.dataset.zStickyReady) return;
    const mainForm = document.querySelector('main product-form form[data-type="add-to-cart-form"]');
    const mainButtons = mainForm?.querySelector('.product-form__buttons');
    const mainVariant = mainForm?.querySelector('input[name="id"]');
    const mainSubmit = mainForm?.querySelector('[type="submit"]');
    const stickyVariant = sticky.querySelector('[data-z-sticky-variant]');
    const stickyButton = sticky.querySelector('button[name="add"]');
    const stickyLabel = stickyButton?.querySelector('span');
    const stickyPrice = sticky.querySelector('[data-z-sticky-price]');
    if (!mainForm || !mainButtons || !mainVariant || !mainSubmit || !stickyVariant || !stickyButton) return;
    sticky.dataset.zStickyReady = 'true';

    const priceSource = () => {
      const price = document.querySelector('main [id^="price-"] .price');
      if (!price) return '';
      const onSale = price.classList.contains('price--on-sale');
      return price.querySelector(onSale ? '.price__sale .price-item--last' : '.price__regular .price-item--regular')?.textContent.trim() || '';
    };
    // Mirror the main form: Dawn updates the variant input first, then the submit button and price.
    const sync = () => {
      const unavailable = !mainVariant.value || mainSubmit.disabled;
      stickyVariant.value = mainVariant.value;
      stickyVariant.disabled = !mainVariant.value;
      stickyButton.disabled = unavailable;
      const label = mainSubmit.querySelector('span')?.textContent.trim();
      if (stickyLabel && label) stickyLabel.textContent = label;
      const price = priceSource();
      if (stickyPrice && price) stickyPrice.textContent = price;
    };
    const syncSoon = () => window.requestAnimationFrame(sync);
    new MutationObserver(syncSoon).observe(mainSubmit, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
    const priceWrapper = document.querySelector('main [id^="price-"]');
    if (priceWrapper) new MutationObserver(syncSoon).observe(priceWrapper, { childList: true, subtree: true, characterData: true });
    mainVariant.addEventListener('change', syncSoon);

    const setVisible = (isActive) => {
      sticky.classList.toggle('is-visible', isActive);
      sticky.inert = !isActive;
      if (isActive) sticky.removeAttribute('aria-hidden'); else sticky.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.toggle('z-sticky-atc-active', isActive);
    };
    const observer = new IntersectionObserver(([entry]) => {
      // Only show once the main buttons have scrolled above the viewport, not before reaching them.
      setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });
    observer.observe(mainButtons);
    setVisible(false);
    sync();
  }

  function initBackToTop() {
    const button = document.querySelector('[data-z-back-to-top]');
    if (!button || button.dataset.zBackReady) return;
    button.dataset.zBackReady = 'true';
    const update = () => button.classList.toggle('is-visible', window.scrollY > 640);
    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    });
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  function init(root = document) {
    window.zoveaMotionReady = true;
    initReveal(root);
    initPointerGlow(root);
    initSunScience(root);
    initProductTilt(root);
    initParticles(root);
    initWordParticles(root);
    initLogoParticles(root);
    initStickyAtc();
    initBackToTop();
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('canvas').forEach((canvas) => canvas.dispatchEvent(new Event('zovea:destroy')));
  });
})();
